/* eslint-disable react/destructuring-assignment */
import type { ContractStatusEnum } from 'gql/graphql';
import { WorkOrderStatusEnum } from 'gql/graphql';
import type { TableColumn } from 'uikit';
import { ColoredColumnTitle } from 'utils/tableCells';

import * as R from 'ramda';
import { useMemo } from 'react';

import type { Row } from '../../WorkOrder/workorderTypes';

import { useWorkOrderVersionSeasons } from './useWorkOrderVersionSeasons';

interface IUseSeasonColumnsProps {
    workOrderId: number;
    onChange: (row: any) => void;
    actsColumns: any[];
    workOrderStatus?: WorkOrderStatusEnum;
    contractStatus?: ContractStatusEnum;
}

export const getSeasonVolumeKey = (id: number) => `seasonPlanVolume_${id}`;
export const getSeasonCostKey = (id: number) => `seasonPlanCost_${id}`;
export const getSeasonPlanVolumeTermsKey = (id: number) => `seasonPlanVolumeTerms_${id}`;

export const getSeasonCompletedVolumeKey = (id: number) => `seasonCompletedVolume_${id}`;
export const getSeasonCompletedCostKey = (id: number) => `seasonCompletedCost_${id}`;

export const getSeasonReminderVolumeKey = (id: number) => `seasonReminderVolume_${id}`;
export const getSeasonReminderCostKey = (id: number) => `seasonReminderCost_${id}`;

// @ts-ignore
const sortLinkedActs = (array: any[]): any[] => R.pipe(R.sortBy(R.prop('id')), R.reverse)(array);

export const showNotNull = (val: any) => (val !== null ? val : '');

export const useWorkOrderVersionSeasonsColumns = (props: IUseSeasonColumnsProps) => {
    const { workOrderStatus, workOrderId, actsColumns } = props;
    const [seasons] = useWorkOrderVersionSeasons(workOrderId);
    const isWorkOrderInProgressOrComplete =
        workOrderStatus && [WorkOrderStatusEnum.InProgress, WorkOrderStatusEnum.Completed].includes(workOrderStatus);

    const seasonsColumns = useMemo(
        () =>
            seasons.reduce((acc: any[], { id, year }: any, index: number) => {
                const isOddSeason = !(index % 2);
                const seasonPlanColumn = {
                    accessor: `Сезон ${year}${isWorkOrderInProgressOrComplete ? ', План' : ''}`,
                    title: (
                        <ColoredColumnTitle isOdd={isOddSeason} align="center">{`Сезон ${year}${
                            isWorkOrderInProgressOrComplete ? ', План' : ''
                        }`}</ColoredColumnTitle>
                    ),
                    withoutPadding: true,
                    columns: [
                        {
                            title: (
                                <ColoredColumnTitle isOdd={isOddSeason} align="left">
                                    Объем
                                </ColoredColumnTitle>
                            ),
                            align: 'right',
                            accessor: getSeasonVolumeKey(id),
                            width: 112,
                            renderCell: (row: Row) => showNotNull((row as any)[getSeasonVolumeKey(id)]),
                        },
                        {
                            title: (
                                <ColoredColumnTitle isOdd={isOddSeason} align="right">
                                    СТОИМОСТЬ, руб. без НДС
                                </ColoredColumnTitle>
                            ),
                            accessor: getSeasonCostKey(id),
                            align: 'right',
                            width: 124,
                            renderCell: (row: Row) => showNotNull((row as any)[getSeasonCostKey(id)]),
                        },
                    ],
                };

                const factAndRemainderColumns = isWorkOrderInProgressOrComplete
                    ? [
                          {
                              title: (
                                  <ColoredColumnTitle
                                      isOdd={isOddSeason}
                                      align="center"
                                  >{`Сезон ${year}, Выполнено`}</ColoredColumnTitle>
                              ),
                              accessor: `Сезон ${year}, Выполнено`,
                              withoutPadding: true,
                              columns: [
                                  {
                                      title: (
                                          <ColoredColumnTitle isOdd={isOddSeason} align="left">
                                              Объем
                                          </ColoredColumnTitle>
                                      ),
                                      align: 'right',
                                      accessor: getSeasonCompletedVolumeKey(id),
                                      width: 112,
                                      renderCell: (row: Row) =>
                                          showNotNull((row as any)[getSeasonCompletedVolumeKey(id)]),
                                  },
                                  {
                                      title: (
                                          <ColoredColumnTitle isOdd={isOddSeason} align="right">
                                              СТОИМОСТЬ, руб. без НДС
                                          </ColoredColumnTitle>
                                      ),
                                      accessor: getSeasonCompletedCostKey(id),
                                      align: 'right',
                                      width: 124,
                                      renderCell: (row: Row) =>
                                          showNotNull((row as any)[getSeasonCompletedCostKey(id)]),
                                  },
                              ],
                          },
                          {
                              title: (
                                  <ColoredColumnTitle
                                      isOdd={isOddSeason}
                                      align="center"
                                  >{`Сезон ${year}, Остаток`}</ColoredColumnTitle>
                              ),
                              withoutPadding: true,
                              accessor: `Сезон ${year}, Остаток`,
                              columns: [
                                  {
                                      title: (
                                          <ColoredColumnTitle isOdd={isOddSeason} align="left">
                                              Объем
                                          </ColoredColumnTitle>
                                      ),
                                      align: 'right',
                                      accessor: getSeasonReminderVolumeKey(id),
                                      width: 112,
                                      renderCell: (row: Row) =>
                                          showNotNull((row as any)[getSeasonReminderVolumeKey(id)]),
                                  },
                                  {
                                      title: (
                                          <ColoredColumnTitle isOdd={isOddSeason} align="right">
                                              СТОИМОСТЬ, руб. без НДС
                                          </ColoredColumnTitle>
                                      ),
                                      accessor: getSeasonReminderCostKey(id),
                                      align: 'right',
                                      width: 124,
                                      renderCell: (row: Row) => showNotNull((row as any)[getSeasonReminderCostKey(id)]),
                                  },
                              ],
                          },
                      ]
                    : [];

                const linkedActs = sortLinkedActs(actsColumns.filter((a) => a.seasonId === id));

                return acc.concat(...[seasonPlanColumn, ...factAndRemainderColumns, ...linkedActs]);
            }, []),
        [seasons, isWorkOrderInProgressOrComplete, actsColumns],
    );

    return seasonsColumns as TableColumn<any>[];
};
